import { fetchJson, useAuth } from '@/lib/auth'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { t } from '@/lib/i18n'

export const Route = createFileRoute('/account')({
  component: AccountPage,
})

function AccountPage() {
  const { user, refresh } = useAuth()
  const navigate = useNavigate()
  // 19 Oct 2026: first login: the user must choose their own password.
  const firstLogin = Boolean(user?.must_change_password)
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [busy, setBusy] = useState(false)

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setSuccess(false)

    if (newPassword.length < 8) {
      setError(t('New password must be at least 8 characters.'))
      return
    }
    if (newPassword !== confirmPassword) {
      setError(t('New passwords do not match.'))
      return
    }

    setBusy(true)
    try {
      const r = await fetchJson('/api/account/change-password', {
        method: 'POST',
        body: JSON.stringify({
          current_password: currentPassword,
          new_password: newPassword,
        }),
      })
      if (!r.ok) {
        const body = await r.json().catch(() => ({}))
        const detail = body.detail
        if (detail === 'wrong_current_password') {
          throw new Error(t('Current password is incorrect.'))
        }
        if (detail === 'same_password') {
          throw new Error(t('Choose a password different from the one you were given.'))
        }
        throw new Error(detail ?? t('Could not change password ({status})', { status: r.status }))
      }
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
      setSuccess(true)
      await refresh()
      if (firstLogin) navigate({ to: '/', replace: true })
    } catch (err) {
      setError(err instanceof Error ? err.message : t('Could not change password'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="max-w-sm mx-auto px-4 sm:px-6 py-8">
      <h1 className="text-lg font-semibold tracking-tight mb-1">{t('Account')}</h1>
      {user && (
        <p className="text-sm text-foreground-muted mb-6" title={user.email}>
          {user.email}
        </p>
      )}

      <form
        onSubmit={onSubmit}
        className="bg-card border border-border rounded-lg p-6 space-y-4"
      >
        <h2 className="text-sm font-medium">{firstLogin ? t('Choose your own password') : t('Change password')}</h2>
        {firstLogin && (
          <p className="text-sm text-foreground-muted" role="status">
            {t('Welcome to Zonoo. Before you start, replace the password you were given with one only you know.')}
          </p>
        )}

        <label className="block text-sm">
          <span className="block text-foreground-muted mb-1">{firstLogin ? t('Password you were given') : t('Current password')}</span>
          <input
            type="password"
            autoComplete="current-password"
            required
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            className="w-full px-3 py-2.5 bg-background border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </label>

        <label className="block text-sm">
          <span className="block text-foreground-muted mb-1">{t('New password')}</span>
          <input
            type="password"
            autoComplete="new-password"
            required
            minLength={8}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            className="w-full px-3 py-2.5 bg-background border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </label>

        <label className="block text-sm">
          <span className="block text-foreground-muted mb-1">{t('Confirm new password')}</span>
          <input
            type="password"
            autoComplete="new-password"
            required
            minLength={8}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            className="w-full px-3 py-2.5 bg-background border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </label>

        {error && (
          <p className="text-sm text-critical" role="alert">
            {error}
          </p>
        )}
        {success && (
          <p className="text-sm text-primary" role="status">
            {t('Password changed.')}
          </p>
        )}

        <button
          type="submit"
          disabled={busy}
          className="w-full py-2.5 rounded-lg bg-primary hover:bg-primary-hover text-primary-foreground font-medium transition-colors disabled:opacity-60"
        >
          {busy ? t('Saving…') : t('Change password')}
        </button>
      </form>
    </div>
  )
}
